import { Button, Heading, Logo } from '@nrg/design-system';
import { useTranslation } from 'react-i18next';
import { LuLock, LuLogOut } from 'react-icons/lu';

import styles from './Forbidden.module.css';

export interface ForbiddenProps {
  /** Signed-in identity, surfaced so the user knows which account is blocked. */
  email?: string;
  /** Invoked when the user logs out — the only action available on this page. */
  onSignOut: () => void;
}

/**
 * Full-screen lockout page shown when the backend refuses the signed-in user
 * (HTTP 403 from `/api/me`). It intentionally replaces the entire app shell:
 * the only available action is logging out.
 */
export default function Forbidden({ email, onSignOut }: ForbiddenProps) {
  const { t } = useTranslation('common');

  return (
    <main className={styles.root}>
      <section className={styles.card}>
        <Logo variant="group" size={44} className={styles.logo} />
        <span className={styles.iconBadge} aria-hidden="true">
          <LuLock className={styles.icon} />
        </span>
        <Heading level={1}>{t('forbidden.title')}</Heading>
        <p className={styles.body}>{t('forbidden.body')}</p>
        {email ? <p className={styles.identity}>{t('forbidden.signedInAs', { email })}</p> : null}
        <p className={styles.contact}>{t('forbidden.contact')}</p>
        <Button
          variant="primary"
          iconLeft={<LuLogOut aria-hidden="true" />}
          onClick={onSignOut}
          className={styles.action}
        >
          {t('logout')}
        </Button>
      </section>
    </main>
  );
}
